import "isomorphic-fetch";
import { capture } from "./instrument";

import { API_URL } from "./config";

class api {
  constructor() {
    this.token = "";
    this.onUnauthorized = () => {};
    this.unauthorizedNotified = false;
    this.tokenListeners = new Set();
  }

  setToken(token) {
    const previousToken = this.token;
    this.token = token;
    if (token) {
      this.unauthorizedNotified = false;
    }
    if (previousToken === token) return;
    const listeners = [...this.tokenListeners];
    for (let i = 0; i < listeners.length; i++) listeners[i](token);
  }

  getToken() {
    return this.token;
  }

  onTokenChange(listener) {
    this.tokenListeners.add(listener);
    return () => this.tokenListeners.delete(listener);
  }

  setUnauthorizedHandler(handler) {
    this.onUnauthorized = handler;
  }

  handleUnauthorized() {
    if (!this.token) return;
    if (this.unauthorizedNotified) return;
    this.unauthorizedNotified = true;
    this.onUnauthorized();
  }

  get(path, { signal, priority } = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          mode: "cors",
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${this.token}`,
          },
          signal,
          priority,
        });

        if (response.status === 401) {
          this.handleUnauthorized();
          return reject({ code: 401, message: "Unauthorized" });
        }

        const res = await response.json();
        if (response.status !== 200) return reject(res);
        resolve(res);
      } catch (e) {
        if (e?.name === "AbortError") return reject(e);
        capture(e);
        reject(e);
      }
    });
  }

  put(path, body) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          mode: "cors",
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${this.token}`,
          },
          body: JSON.stringify(body),
        });

        if (response.status === 401) {
          this.handleUnauthorized();
          return reject({ code: 401, message: "Unauthorized" });
        }

        const res = await response.json();
        if (response.status !== 200) return reject(res);
        resolve(res);
      } catch (e) {
        capture(e);
        reject(e);
      }
    });
  }

  putFormData(path, formData) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          mode: "cors",
          method: "PUT",
          credentials: "include",
          headers: { Authorization: `JWT ${this.token}` },
          body: formData,
        });

        if (response.status === 401) {
          this.handleUnauthorized();
          return reject({ code: 401, message: "Unauthorized" });
        }

        const res = await response.json();
        if (response.status !== 200) return reject(res);
        resolve(res);
      } catch (e) {
        capture(e);
        reject(e);
      }
    });
  }

  postFormData(path, formData) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          mode: "cors",
          method: "POST",
          credentials: "include",
          headers: { Authorization: `JWT ${this.token}` },
          body: formData,
        });
        if (response.status === 401) {
          this.handleUnauthorized();
          return reject({ code: 401, message: "Unauthorized" });
        }

        const res = await response.json();
        if (response.status !== 200) return reject(res);
        resolve(res);
      } catch (e) {
        capture(e);
        reject(e);
      }
    });
  }

  delete(path, body) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          mode: "cors",
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${this.token}`,
          },
          body: JSON.stringify(body),
        });

        if (response.status === 401) {
          this.handleUnauthorized();
          return reject({ code: 401, message: "Unauthorized" });
        }

        const res = await response.json();
        if (response.status !== 200) return reject(res);
        resolve(res);
      } catch (e) {
        capture(e);
        reject(e);
      }
    });
  }

  post(path, body) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          mode: "cors",
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${this.token}`,
          },
          body: JSON.stringify(body),
        });

        if (response.status === 401) {
          this.handleUnauthorized();
          return reject({ code: 401, message: "Unauthorized" });
        }

        const res = await response.json();
        if (response.status !== 200) return reject(res);
        resolve(res);
      } catch (e) {
        capture(e);
        reject(e);
      }
    });
  }

  download(path) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(`${API_URL}${path}`, {
          mode: "cors",
          method: "GET",
          credentials: "include",
          headers: { Authorization: `JWT ${this.token}` },
        });

        if (response.status === 401) {
          this.handleUnauthorized();
          return reject({ code: 401, message: "Unauthorized" });
        }

        const res = await response.blob();
        if (response.status !== 200) return reject(res);
        resolve(res);
      } catch (e) {
        capture(e);
        reject(e);
      }
    });
  }
}

const service = new api();
export default service;
